/**
 * Places API (New) - REST API utility
 * Uses field masking to only fetch name and phone number
 * This reduces API costs significantly
 */

const PLACES_API_BASE_URL = 'https://places.googleapis.com/v1';
const API_KEY = 'AIzaSyAOCYuFjZujDZ8JGdXopIw-eoyjFGD471U';

// Field mask for minimal data - name, phone, and address
// Places API (New) uses field paths like: places.id, places.displayName, etc.
const TEXT_SEARCH_FIELDS = 'places.id,places.displayName,places.nationalPhoneNumber,places.internationalPhoneNumber,places.formattedAddress';
const PLACE_DETAILS_FIELDS = 'id,displayName,nationalPhoneNumber,internationalPhoneNumber,formattedAddress,location';

/**
 * Text Search (New) - Search for places by text query
 * @param {Object} params - Search parameters
 * @param {string} params.textQuery - The text query to search for
 * @param {Object} params.location - Location object with lat/lng
 * @param {number} params.radius - Search radius in meters
 * @returns {Promise<Array>} Array of place results
 */
export const textSearchNew = async ({ textQuery, location, radius = 10000 }) => {
  try {
    const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
    const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

    const requestBody = {
      textQuery: textQuery,
      maxResultCount: 20, // Maximum results per request
      locationBias: {
        circle: {
          center: {
            latitude: lat,
            longitude: lng
          },
          radius: radius
        }
      },
      languageCode: 'en'
    };

    const response = await fetch(`${PLACES_API_BASE_URL}/places:searchText`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
        'X-Goog-FieldMask': TEXT_SEARCH_FIELDS
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Places API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    
    // Return array of places
    if (data.places && Array.isArray(data.places)) {
      return data.places;
    }
    
    return [];
  } catch (error) {
    console.error('Error in textSearchNew:', error);
    throw error;
  }
};

/**
 * Place Details (New) - Get details for a specific place
 * @param {string} placeId - The place ID
 * @returns {Promise<Object>} Place details object
 */
export const getPlaceDetailsNew = async (placeId) => {
  try {
    const response = await fetch(`${PLACES_API_BASE_URL}/places/${placeId}`, {
      method: 'GET',
      headers: {
        'X-Goog-Api-Key': API_KEY,
        'X-Goog-FieldMask': PLACE_DETAILS_FIELDS
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Places API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in getPlaceDetailsNew:', error);
    throw error;
  }
};

/**
 * Autocomplete (New) - Get place predictions based on input
 * @param {Object} params - Autocomplete parameters
 * @param {string} params.input - The input string to get predictions for
 * @param {Object} params.location - Optional location bias (lat/lng)
 * @param {string} params.languageCode - Language code (default: 'en')
 * @returns {Promise<Array>} Array of place predictions
 */
export const autocompleteNew = async ({ input, location = null, languageCode = 'en' }) => {
  try {
    if (!input || input.trim().length < 2) {
      return [];
    }

    const requestBody = {
      input: input.trim(),
      languageCode: languageCode,
      includedRegionCodes: ['in'] // Restrict to India
    };

    // Add location bias if provided
    if (location) {
      const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
      const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
      
      if (lat && lng) {
        requestBody.locationBias = {
          circle: {
            center: {
              latitude: lat,
              longitude: lng
            },
            radius: 50000 // 50km radius for bias
          }
        };
      }
    }

    const response = await fetch(`${PLACES_API_BASE_URL}/places:autocomplete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
        'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Places API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    
    // Return array of predictions
    if (data.suggestions && Array.isArray(data.suggestions)) {
      return data.suggestions
        .filter(suggestion => suggestion.placePrediction)
        .map(suggestion => ({
          placeId: suggestion.placePrediction.placeId,
          text: suggestion.placePrediction.text?.text || suggestion.placePrediction.structuredFormat?.mainText?.text || '',
          description: suggestion.placePrediction.structuredFormat?.secondaryText?.text || ''
        }));
    }
    
    return [];
  } catch (error) {
    console.error('Error in autocompleteNew:', error);
    throw error;
  }
};

/**
 * Format place data from Places API (New) response
 * @param {Object} place - Place object from API
 * @returns {Object|null} Formatted lead data or null if missing required fields
 */
export const formatPlaceDataNew = (place) => {
  if (!place || !place.id) {
    return null;
  }

  // Get phone number (prefer national, fallback to international)
  const phone = place.nationalPhoneNumber || place.internationalPhoneNumber || null;
  
  // Must have phone number
  if (!phone) {
    return null;
  }

  // Get display name - Places API (New) returns displayName as an object with text property
  const name = place.displayName?.text || place.displayName || 'N/A';
  
  // Get formatted address
  const address = place.formattedAddress || null;

  return {
    name: name,
    phone: phone,
    placeId: place.id,
    address: address,
    // Remove fields we don't need
    email: null,
    website: null,
    coordinates: null,
    lat: null,
    lng: null,
    rating: null,
    userRatingsTotal: 0,
    businessStatus: null,
    types: [],
    priority: 0, // Will be set by getPriority function
    contacted: false
  };
};

